const STEP = 5;
const CEILING = 9999;
const FALLBACK_MIN = 20;

const BRL = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
const money = (cents) => BRL.format((cents || 0) / 100).replace(/ /g, ' ');

export function createCartPhotos({ root, cart, sheet, host, fetch, track, notify, pathname, newGroupId }) {
  const drawer = root.getElementById('cart-drawer');
  if (!drawer || !cart) return null;

  const addUrl = drawer.dataset.cartAddUrl;
  const cartJsonUrl = (drawer.dataset.cartUrl || '/cart') + '.js';
  const copy = { added: drawer.dataset.noticeAdded || '' };
  const errorNode = drawer.querySelector('[data-cart-error]');
  const genericError = errorNode ? (errorNode.dataset.errorGeneric || '') : '';
  const sheetNode = root.querySelector('[data-cart-sheet]');
  const laterCopy = (sheetNode && sheetNode.dataset.sheetLater) || '';

  const el = (tag, attributes, ...children) => {
    const node = root.createElement(tag);
    Object.keys(attributes || {}).forEach((name) => {
      const value = attributes[name];
      if (value === false || value === null || value === undefined) return;
      if (name === 'text') node.textContent = value;
      else if (name === 'hidden') node.hidden = true;
      else if (name === 'disabled') node.disabled = true;
      else node.setAttribute(name, value === true ? '' : String(value));
    });
    node.append(...children.filter(Boolean));
    return node;
  };

  async function readIsland(productUrl, selector) {
    const productDocument = await host.loadProductDocument(productUrl);
    const island = selector.startsWith('#')
      ? productDocument.getElementById(selector.slice(1))
      : productDocument.querySelector(selector);
    if (!island) throw new Error('cart-photos: island ' + selector + ' missing on ' + productUrl);
    return JSON.parse(island.textContent);
  }

  async function loadAddon(productUrl) {
    const config = await readIsland(productUrl, '#pdp-config');
    const addon = config.photoAddon;
    if (!addon || !Array.isArray(addon.sizes) || !addon.sizes.length) throw new Error('cart-photos: no photoAddon');
    return addon;
  }

  async function loadTradIsland(tradUrl) {
    if (!tradUrl) return null;
    try {
      const config = await readIsland(tradUrl, '.pf-config-data');
      return {
        borderLabel: (config.strings && config.strings.borderWith) || '',
        variants: Array.isArray(config.variants) ? config.variants : [],
      };
    } catch {
      return null;
    }
  }

  function borderFor(trad, tab, sizeId) {
    if (tab !== 'trad' || !trad || !trad.borderLabel) return '';
    const variant = trad.variants.find((entry) => entry.label === sizeId + 'cm');
    const bordered = variant ? Boolean(variant.showBorder) : sizeId !== '5x6';
    return bordered ? trad.borderLabel : '';
  }

  async function quantityInBagByVariant() {
    const response = await fetch(cartJsonUrl, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('cart-photos: cart read failed');
    const data = await response.json();
    const sums = {};
    (data.items || []).forEach((item) => { sums[String(item.variant_id)] = (sums[String(item.variant_id)] || 0) + (Number(item.quantity) || 0); });
    return sums;
  }

  async function post(url, payload) {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...payload, sections: 'cart-drawer', sections_url: pathname }),
    });
    if (!response.ok) throw new Error('cart-photos: write failed');
    return response.json();
  }

  const render = (data) => {
    if (!cart.replaceContent(data && data.sections && data.sections['cart-drawer'])) cart.reload();
  };

  const accordionOf = (group) => Array.from(drawer.querySelectorAll('[data-upsell]')).find((node) => node.dataset.group === group) || null;

  const variantFor = (size, tab) => (size.variantIds && size.variantIds[tab]) || null;
  const sizesFor = (addon, tab) => addon.sizes.filter((size) => variantFor(size, tab));
  const minFor = (addon, size) => Number(size.minQty) || Number(addon.minQty) || FALLBACK_MIN;
  const clamp = (addon, size, quantity) => {
    const min = minFor(addon, size);
    const ceiling = Math.min(CEILING, Number(addon.maxQty) || CEILING);
    return Math.max(min, Math.floor(Math.min(ceiling, quantity || min)));
  };
  const tierFor = (addon, quantity) => {
    let best = null;
    (addon.tiers || []).forEach((tier) => { if (quantity >= tier.q && (!best || tier.q > best.q)) best = tier; });
    return best;
  };
  const priceOf = (addon, size, tab, quantity, inBag) => {
    const unit = (size.prices && size.prices[tab]) || 0;
    const already = (inBag && inBag[String(variantFor(size, tab))]) || 0;
    const tier = tierFor(addon, quantity + already);
    const pct = tier ? tier.p : 0;
    const unitDisc = Math.round(unit * (1 - pct / 100));
    return { unit, unitDisc, pct, total: unitDisc * quantity, full: unit * quantity };
  };

  function buildPopup(trigger, addon, trad, inBag) {
    const data = trigger.dataset;
    const group = data.group;
    const family = (trigger.closest('[data-upsell]') || { dataset: {} }).dataset.family || '';
    const tabs = (addon.tabs || []).filter((tab) => sizesFor(addon, tab.id).length);
    const firstTab = tabs[0] ? tabs[0].id : null;
    const firstSize = firstTab ? sizesFor(addon, firstTab)[0] : null;
    const state = {
      tab: firstTab,
      size: firstSize ? firstSize.id : null,
      qty: 0,
      draft: null,
    };
    const currentSize = () => addon.sizes.find((size) => size.id === state.size) || null;
    const suggestionFor = (size) => (size && size.suggestion && Number(size.suggestion[state.tab])) || 0;
    const startQty = (size) => clamp(addon, size, suggestionFor(size) || Number(addon.featuredQty) || 0);
    if (firstSize) state.qty = startQty(firstSize);

    const tabButtons = tabs.map((tab) => el('button', { type: 'button', role: 'tab', class: 'cart-photos__tab', 'data-photos-tab': tab.id, text: tab.label || tab.id }));
    const tabList = el('div', { class: 'cart-photos__tabs', role: 'tablist' }, ...tabButtons);
    const sizeList = el('div', { class: 'cart-photos__sizes' });
    const suggestion = el('button', { type: 'button', class: 'cart-photos__suggestion', 'data-photos-suggestion': true, hidden: true });
    const featured = el('span', { class: 'cart-photos__muted', 'data-photos-featured': true, hidden: true });
    const packsHead = el('div', { class: 'cart-photos__row-head' },
      el('span', { class: 'cart-photos__label', text: addon.packagesLabel || '' }),
      featured);
    const packs = el('div', { class: 'cart-photos__packs' }, ...(addon.tiers || []).map((tier) => el('button', { type: 'button', class: 'cart-photos__pack', 'data-photos-pack': tier.q },
      el('b', { text: tier.q + ' un' }),
      el('small', { text: tier.p + (addon.offSuffix || '') }))));
    const minLabel = el('span', { class: 'cart-photos__muted', 'data-photos-min': true });
    const qtyHead = el('div', { class: 'cart-photos__row-head' }, el('span', { class: 'cart-photos__label', text: addon.qtyLabel || '' }), minLabel);
    const input = el('input', { class: 'cart-photos__input', type: 'text', inputmode: 'numeric', pattern: '[0-9]*', maxlength: '4', autocomplete: 'off', 'data-photos-input': true, 'aria-label': addon.qtyLabel || '' });
    const unitPrice = el('span', { class: 'cart-photos__unit', 'data-photos-unit': true });
    const stepper = el('div', { class: 'cart-photos__qty' },
      el('button', { type: 'button', class: 'cart-photos__step', 'data-photos-step': -STEP, 'aria-label': '−' + STEP, text: '−' }),
      input,
      el('button', { type: 'button', class: 'cart-photos__step', 'data-photos-step': STEP, 'aria-label': '+' + STEP, text: '+' }),
      unitPrice);
    const priceNow = el('b', { 'data-photos-total': true });
    const priceFull = el('s', { 'data-photos-full': true, hidden: true });
    const priceOff = el('span', { class: 'cart-photos__off', 'data-photos-off': true, hidden: true });
    const priceLine = el('p', { class: 'cart-photos__price' }, priceNow, priceFull, priceOff);
    const cta = el('button', { type: 'button', class: 'cart-sheet__cta', 'data-photos-cta': true });
    const secondary = el('button', { type: 'button', class: 'cart-sheet__later', 'data-photos-secondary': true, text: laterCopy });

    const popup = el('div', { class: 'cart-photos' },
      data.sheetDesc ? el('p', { class: 'cart-photos__hint', text: data.sheetDesc }) : null,
      tabList, sizeList, suggestion, packsHead, packs, qtyHead, stepper, priceLine, cta, secondary);

    function paintSizes() {
      const sizes = sizesFor(addon, state.tab);
      sizeList.replaceChildren(...sizes.map((size) => {
        const unit = (size.prices && size.prices[state.tab]) || 0;
        const thumb = size.thumbs && size.thumbs[state.tab];
        const button = el('button', { type: 'button', class: 'cart-photos__size' + (size.id === state.size ? ' is-active' : ''), 'data-photos-size': size.id, 'aria-pressed': size.id === state.size ? 'true' : 'false' },
          thumb ? el('img', { class: 'cart-photos__thumb', src: thumb, alt: '', width: '44', height: '44', loading: 'lazy' }) : null,
          el('span', { class: 'cart-photos__size-text' },
            el('b', { text: size.label || (size.id + ' cm') }),
            el('small', { text: (data.photosPerPhotoShort || '{price}').replace('{price}', money(unit)) })));
        return button;
      }));
      sizeList.classList.toggle('cart-photos__sizes--many', sizes.length > 2);
    }

    const draftQty = () => (state.draft === null ? NaN : parseInt(state.draft, 10));
    const previewQty = (size) => (state.draft === null ? state.qty : clamp(addon, size, draftQty()));
    const draftBelowMin = (size) => state.draft !== null && draftQty() < minFor(addon, size);

    function paint() {
      const size = currentSize();
      const qty = previewQty(size);
      tabButtons.forEach((button) => {
        const on = button.dataset.photosTab === state.tab;
        button.classList.toggle('is-active', on);
        button.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      paintSizes();
      const sugg = suggestionFor(size);
      suggestion.hidden = !(sugg > 0);
      suggestion.textContent = sugg > 0 ? (addon.suggestionLabel || '{n}').replace('{n}', String(sugg)) : '';
      suggestion.dataset.photosSuggestion = String(sugg);
      const showFeatured = sugg > 0 && !!addon.recommendedLabel;
      featured.hidden = !showFeatured;
      featured.textContent = showFeatured ? addon.recommendedLabel.replace('{n}', String(sugg)) : '';
      const price = size ? priceOf(addon, size, state.tab, qty, inBag) : null;
      const reachedPack = tierFor(addon, qty);
      Array.from(packs.querySelectorAll('[data-photos-pack]')).forEach((pill) => {
        pill.classList.toggle('is-active', !!price && Number(pill.dataset.photosPack) === (reachedPack ? reachedPack.q : -1));
      });
      minLabel.textContent = size ? (addon.minLabel || '{n}').replace('{n}', String(minFor(addon, size))) : '';
      const belowMin = draftBelowMin(size);
      minLabel.classList.toggle('is-warning', belowMin);
      if (belowMin) input.setAttribute('aria-invalid', 'true');
      else input.removeAttribute('aria-invalid');
      if (state.draft === null) input.value = String(state.qty);
      unitPrice.textContent = price ? (data.photosPerPhoto || '{price}').replace('{price}', money(price.unitDisc)) : '';
      priceNow.textContent = price ? money(price.total) : '';
      priceFull.textContent = price && price.pct ? money(price.full) : '';
      priceFull.hidden = !(price && price.pct);
      priceOff.textContent = price && price.pct ? '-' + price.pct + '%' : '';
      priceOff.hidden = !(price && price.pct);
      cta.textContent = price ? (data.ctaAdd || '{price}').replace('{price}', money(price.total)) : '';
      cta.disabled = !size || !variantFor(size, state.tab) || !qty;
    }

    const commitQty = (quantity) => {
      state.draft = null;
      state.qty = clamp(addon, currentSize(), quantity);
      paint();
    };

    tabButtons.forEach((button) => button.addEventListener('click', () => {
      state.tab = button.dataset.photosTab;
      const sizes = sizesFor(addon, state.tab);
      if (!sizes.some((size) => size.id === state.size)) state.size = sizes[0] ? sizes[0].id : null;
      commitQty(startQty(currentSize()));
    }));
    sizeList.addEventListener('click', (event) => {
      const button = event.target.closest('[data-photos-size]');
      if (!button) return;
      state.size = button.dataset.photosSize;
      commitQty(startQty(currentSize()));
    });
    suggestion.addEventListener('click', () => commitQty(Number(suggestion.dataset.photosSuggestion)));
    packs.addEventListener('click', (event) => {
      const pill = event.target.closest('[data-photos-pack]');
      if (pill) commitQty(Number(pill.dataset.photosPack));
    });
    stepper.addEventListener('click', (event) => {
      const button = event.target.closest('[data-photos-step]');
      if (!button) return;
      const base = state.draft === null ? state.qty : clamp(addon, currentSize(), parseInt(state.draft, 10));
      commitQty(base + Number(button.dataset.photosStep));
    });
    input.addEventListener('input', () => {
      const digits = String(input.value || '').replace(/\D/g, '');
      if (digits !== input.value) input.value = digits;
      state.draft = digits;
      paint();
    });
    input.addEventListener('blur', () => commitQty(parseInt(String(input.value || ''), 10)));
    input.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      if (typeof input.blur === 'function') input.blur();
      commitQty(parseInt(String(input.value || ''), 10));
    });
    cta.addEventListener('click', () => {
      const size = currentSize();
      if (!size) return;
      if (state.draft !== null) {
        const typed = draftQty();
        commitQty(typed);
        if (state.qty !== typed) return;
      }
      const quantity = state.qty;
      const chosen = { tab: state.tab, size, quantity, variantId: variantFor(size, state.tab), price: priceOf(addon, size, state.tab, quantity, inBag), border: borderFor(trad, state.tab, size.id) };
      sheet.close();
      commit(trigger, chosen, { group, family });
    });
    secondary.addEventListener('click', () => sheet.close());

    paint();
    return popup;
  }

  function commit(trigger, chosen, { group, family }) {
    const data = trigger.dataset;
    cart.hideError();
    return cart.enqueueWrite(async () => {
      if (!accordionOf(group)) return;
      const properties = {
        _group: newGroupId(),
        'Tamanho': chosen.size.id + 'cm',
        'Quantidade': String(chosen.quantity),
      };
      if (chosen.border) properties['Borda'] = chosen.border;
      properties._parent_group = group;
      properties._addon_origin = 'cart';
      try {
        const painted = await post(addUrl, { items: [{ id: Number(chosen.variantId), quantity: chosen.quantity, properties }] });
        track('cart_upsell_add', { addon: 'fotos', value_cents: chosen.price.total, origin: 'cart', family });
        render(painted);
        if (copy.added) {
          const name = data['photosName' + (chosen.tab === 'trad' ? 'Trad' : 'Auto')] || '';
          const subtitle = (data.noticeSubtitle || '')
            .replace('{name}', name)
            .replace('{size}', chosen.size.id)
            .replace('{count}', String(chosen.quantity));
          notify({ title: copy.added, subtitle, group });
        }
      } catch {
        cart.showError();
      }
    });
  }

  function editChild(trigger) {
    const childGroup = trigger.dataset.childGroup;
    if (!childGroup) return false;
    const line = Array.from(drawer.querySelectorAll('.cart-item')).find((node) => node.dataset.group === childGroup);
    const link = line && line.querySelector('[data-edit-surface]');
    if (!link) return false;
    link.click();
    return true;
  }

  async function openPopup(trigger) {
    const token = sheet.stamp();
    const family = (trigger.closest('[data-upsell]') || { dataset: {} }).dataset.family || '';
    trigger.setAttribute('aria-busy', 'true');
    let addon = null;
    let trad = null;
    let inBag = {};
    try {
      [addon, trad, inBag] = await Promise.all([loadAddon(trigger.dataset.productUrl), loadTradIsland(trigger.dataset.photosTradUrl), quantityInBagByVariant()]);
    } catch {
      addon = null;
    } finally {
      trigger.removeAttribute('aria-busy');
    }
    if (!sheet.isCurrent(token)) return;
    const content = addon
      ? buildPopup(trigger, addon, trad, inBag)
      : el('div', { class: 'cart-photos' },
        el('p', { class: 'cart-photos__status', role: 'status', text: genericError }),
        el('button', { type: 'button', class: 'cart-sheet__later', 'data-photos-secondary': true, text: laterCopy }));
    if (!addon) content.querySelector('[data-photos-secondary]').addEventListener('click', () => sheet.close());
    sheet.openCustom({
      title: trigger.dataset.sheetHeading || trigger.dataset.noticeName || '',
      opener: trigger,
      sheet: 'fotos',
      family,
      group: trigger.dataset.group,
      mountInto: (node) => node.append(content),
    });
  }

  drawer.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-action="upsell-photos"], [data-action="upsell-photos-edit"]');
    if (!trigger || !drawer.contains(trigger)) return;
    event.preventDefault();
    if (trigger.dataset.action === 'upsell-photos-edit') editChild(trigger);
    else openPopup(trigger);
  });

  return { openPopup, editChild };
}

if (typeof document !== 'undefined') {
  const start = () => {
    createCartPhotos({
      root: document,
      cart: window.fnCart,
      sheet: {
        openCustom: (options) => window.fnCartSheet && window.fnCartSheet.openCustom(options),
        close: () => window.fnCartSheet && window.fnCartSheet.close(),
        stamp: () => (window.fnCartSheet ? window.fnCartSheet.stamp() : 0),
        isCurrent: (token) => (window.fnCartSheet ? window.fnCartSheet.isCurrent(token) : true),
      },
      host: {
        loadProductDocument: (url) => (window.fnCartEdit
          ? window.fnCartEdit.loadProductDocument(url)
          : Promise.reject(new Error('cart-photos: no edit host'))),
      },
      fetch: (url, init) => window.fetch(url, init),
      track: (event, payload) => {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event, ...payload });
      },
      notify: (payload) => {
        if (window.fnCartNotice) window.fnCartNotice.show(payload);
      },
      pathname: window.location.pathname,
      newGroupId: () => ((window.crypto && window.crypto.randomUUID)
        ? window.crypto.randomUUID()
        : 'g' + Date.now() + Math.round(Math.random() * 1e6)),
    });
  };
  if (window.fnCart) start();
  else document.addEventListener('fn:cart-ready', start, { once: true });
}
